import {
  ApiProjections,
  Cart,
  ErrorResponse,
  SubCarts,
  cartApi,
  getCurrentCartId,
  OrderType,
} from '@commerce-cart-webcomponents/sdk';
import { useEffect, useState } from 'react';
import { SubscriptionType } from '@commerce-subscription-webcomponents/sdk';
import { Logger } from '@nextgen-web-framework/core';
import { useSession } from '@nextgen-web-framework/all';
import logObject from '../../../../logObject';
import { amwayUserData } from '../../../utils/utils';
import { EnableProxyOrderConsumerType, EnableProxyOrderPropTypes } from './types/propTypes';

const useEnableProxyOrder = ({
  consumer,
  subscriptionDetails,
  addToCartBtnClicked,
  onEnablement,
}: EnableProxyOrderPropTypes) => {
  const loggerServiceInstance = Logger.getInstance(logObject('useEnableProxyOrder'));

  const [isModeSwitchNeeded, setIsModeSwitchNeeded] = useState(false);
  const [isCartPresent, setIsCartPresent] = useState(false);
  const [subCart, setSubCart] = useState<SubCarts | undefined>(undefined);
  const [cartId, setCartId] = useState('');
  const [cartVersion, setCartVersion] = useState<number>(0);
  const [currentOrderType, setCurrentOrderType] = useState<OrderType>(OrderType.Regular);
  const [newOrderType, setNewOrderType] = useState<OrderType>(OrderType.Regular);
  const [isCartDetailsFetched, setIsCartDetailsFetched] = useState(false);
  const isManualSubscription = subscriptionDetails?.type === SubscriptionType.Manual;
  const { session } = useSession();
  const amwayUser = amwayUserData(session);
  /* Check if the subscription is for the current user (self) or a downline
     If the subscription's amwayUser accountId doesn't match the current user's accountId, it's for a downline. */
  const isSubscriptionForDownline = subscriptionDetails?.amwayUser?.accountId !== amwayUser?.accountId;

  // Check if cart is preset for subscribed user
  const handleSubCarts = (cartDetails: Cart) => {
    const filteredCarts = cartDetails.subCarts.filter(
      (subCart) => subCart.amwayUser?.accountId === subscriptionDetails?.amwayUser?.accountId,
    ) as SubCarts[];

    if (filteredCarts.length > 0) {
      setIsCartPresent(true);
      setSubCart(filteredCarts[0]);
      determineProxyOrder(cartDetails, filteredCarts[0]);
    } else {
      setIsCartPresent(false);
      setSubCart(undefined);
      determineProxyOrder(cartDetails);
    }
  };

  // Determine if proxy order should be enabled based on subscription type and cart details
  const determineProxyOrder = (cartDetails: Cart, subCart?: SubCarts) => {
    const cartData = cartDetails as Cart & { orderType: OrderType };
    if (!isSubscriptionForDownline) {
      // Subscribing for self and current cart state is PROXY for different user
      if (cartData.orderType === OrderType.Proxy) {
        setNewOrderType(OrderType.Regular);
        setIsModeSwitchNeeded(true);
        if (onEnablement) {
          onEnablement();
        }
      }
    } else {
      // Subscribing for downline and current cart state is Regular (My Cart)
      if (cartData.orderType === OrderType.Regular) {
        // If subscribing for downline and cart is REGULAR, thus enable proxy mode
        setNewOrderType(OrderType.Proxy);
        setIsModeSwitchNeeded(true);
        if (onEnablement) {
          onEnablement();
        }
      } else if (cartData.orderType === OrderType.Proxy) {
        const subCartToCheck = subCart as SubCarts & { proxySubCart: boolean };
        const proxyCartForSubscribingUser = subCartToCheck?.proxySubCart;
        if (!proxyCartForSubscribingUser) {
          // If current cart state is PROXY and it is for a different user, thus proxy mode for other downline
          setNewOrderType(OrderType.Proxy);
          setIsModeSwitchNeeded(true);
          if (onEnablement) {
            onEnablement();
          }
        }
      }
    }
  };

  const fetchCartData = async () => {
    const cartId = await getCurrentCartId();

    cartApi
      .getCart({
        cartId: cartId,
        projections: ApiProjections.FULL,
      })
      .then((response) => {
        const cartDetails = response as Cart;
        if (cartDetails.id) {
          setCartId(cartDetails.id);
          setCartVersion(cartDetails.version);
          setCurrentOrderType(cartDetails.orderType);
          handleSubCarts(cartDetails);
          setIsCartDetailsFetched(true);
        }
      })
      .catch((response) => {
        const error = response as ErrorResponse;
        loggerServiceInstance.error('get cart error', error);
      });
  };

  useEffect(() => {
    // Fetch data if it's a manual subscription and user is ABO
    if (consumer === EnableProxyOrderConsumerType.SubscriptionCheckout && isManualSubscription) {
      fetchCartData();
    }
  }, [subscriptionDetails?.id]);

  useEffect(() => {
    if (consumer === EnableProxyOrderConsumerType.AddToCartButton && addToCartBtnClicked) {
      fetchCartData();
    }
  }, [addToCartBtnClicked]);

  // Return the state indicating if proxy order is enabled
  return {
    isModeSwitchNeeded,
    isCartPresent,
    cartId,
    cartVersion,
    subCart,
    currentOrderType,
    newOrderType,
    isCartDetailsFetched,
  };
};

export { useEnableProxyOrder };
